import type { TypedDocumentNode } from '@apollo/client'
import { parse } from 'graphql'

export interface ProductCatalogOffer {
  __typename?: string
  id: string
  type: string
  cost: number
  costWithoutVat: number | null
  period: number | null
  currency: string
}

export interface ProductCatalogTax {
  __typename?: string
  taxName: string
  taxValue: number
}

export interface ProductCatalogProductAttribute {
  __typename?: string
  id: string
  name: string
  value: string
}

export interface ProductCatalogProductSpecification {
  __typename?: string
  id: string
  groupName: string
  name: string
  value: string
}

export interface ProductCatalogProduct {
  __typename?: string
  id: string
  brand: string | null
  description: string | null
  name: string | null
  typeName: string | null
  productSpecifications: ProductCatalogProductSpecification[]
}

export interface ProductCatalogVariant {
  __typename?: string
  id: string
  deliveryConditions: string | null
  description: string | null
  distributorId: string
  firstOfferCost: number
  firstOfferId: string
  firstOfferPeriod: number | null
  firstProductMediaImageUrl: string | null
  product: ProductCatalogProduct | null
  offers: ProductCatalogOffer[]
  productTaxes: ProductCatalogTax[]
  productAttributes: ProductCatalogProductAttribute[]
  productMediaImageUrls?: string[]
  sku: string | null
  stock: number | null
  upc: string | null
}

export interface ProductCatalogVariantDetail extends ProductCatalogVariant {
  productMediaImageUrls: string[]
}

export interface ProductCatalogVariantDetailQuery {
  itManagement: {
    distributorProductVariantsConnection: {
      nodes: ProductCatalogVariantDetail[]
      totalCount: number
    }
  }
}

export interface ProductCatalogVariantsQuery {
  itManagement: {
    distributorProductVariantsConnection: {
      nodes: ProductCatalogVariant[]
      totalCount: number
    }
  }
}

export interface ProductCatalogVariantsQueryVariables {
  filter?: Record<string, unknown> | null
  ids?: string[] | null
  page?: number | null
  perPage?: number | null
  search?: string | null
}

export interface ProductCatalogCharacteristicOption {
  name: string
  quantity: number
}

export interface ProductCatalogCharacteristicGroup {
  category: string
  options: ProductCatalogCharacteristicOption[]
}

export interface ProductCatalogCharacteristicGroupsQuery {
  itManagement: {
    distributorProductVariantCharacteristicGroups: ProductCatalogCharacteristicGroup[]
  }
}

export interface ProductCatalogCharacteristicGroupsQueryVariables {
  category: string
}

export const PRODUCT_CATALOG_VARIANTS_DOCUMENT: TypedDocumentNode<
  ProductCatalogVariantsQuery,
  ProductCatalogVariantsQueryVariables
> = parse(`
  query ProductCatalogVariants(
    $filter: ItManagementDistributorProductVariantFilter
    $ids: [String!]
    $page: Int
    $perPage: Int
    $search: String
  ) {
    itManagement {
      distributorProductVariantsConnection(
        filter: $filter
        first: $perPage
        ids: $ids
        offsetPage: $page
        search: $search
      ) {
        nodes {
          id
          deliveryConditions
          description
          distributorId
          firstOfferCost
          firstOfferId
          firstOfferPeriod
          firstProductMediaImageUrl
          product {
            id
            brand
            description
            name
            typeName
            productSpecifications {
              id
              groupName
              name
              value
            }
          }
          offers {
            id
            type
            cost
            costWithoutVat
            period
            currency
          }
          productTaxes {
            taxName
            taxValue
          }
          productAttributes {
            id
            name
            value
          }
          sku
          stock
          upc
        }
        totalCount
      }
    }
  }
`)

export const PRODUCT_CATALOG_VARIANT_DETAIL_DOCUMENT: TypedDocumentNode<
  ProductCatalogVariantDetailQuery,
  ProductCatalogVariantsQueryVariables
> = parse(`
  query ProductCatalogVariantDetail(
    $filter: ItManagementDistributorProductVariantFilter
    $ids: [String!]
    $page: Int
    $perPage: Int
    $search: String
  ) {
    itManagement {
      distributorProductVariantsConnection(
        filter: $filter
        first: $perPage
        ids: $ids
        offsetPage: $page
        search: $search
      ) {
        nodes {
          id
          deliveryConditions
          description
          distributorId
          firstOfferCost
          firstOfferId
          firstOfferPeriod
          firstProductMediaImageUrl
          product {
            id
            brand
            description
            name
            typeName
            productSpecifications {
              id
              groupName
              name
              value
            }
          }
          offers {
            id
            type
            cost
            costWithoutVat
            period
            currency
          }
          productTaxes {
            taxName
            taxValue
          }
          productAttributes {
            id
            name
            value
          }
          productMediaImageUrls
          sku
          stock
          upc
        }
        totalCount
      }
    }
  }
`)

export const PRODUCT_CATALOG_CHARACTERISTIC_GROUPS_DOCUMENT: TypedDocumentNode<
  ProductCatalogCharacteristicGroupsQuery,
  ProductCatalogCharacteristicGroupsQueryVariables
> = parse(`
  query ProductCatalogCharacteristicGroups($category: String!) {
    itManagement {
      distributorProductVariantCharacteristicGroups(category: $category) {
        category
        options {
          name
          quantity
        }
      }
    }
  }
`)
